import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

import {
  FaMapMarkerAlt,
  FaPaperPlane,
  FaGlobeAmericas,
  FaChevronRight,
  FaCheckCircle,
  FaStar,
  FaChevronDown,
  FaTimesCircle,
  FaClock,
  FaBus,
  FaHotel,
  FaUtensils,
  FaShieldAlt,
  FaMountain
} from 'react-icons/fa';

import './HimalayanNepal.css';

const HimalayanNepal = () => {

  const [openSection, setOpenSection] = useState(null);

  const itineraryRef = useRef(null);
  const highlightsRef = useRef(null);
  const inclusionsRef = useRef(null);
  const aboutRef = useRef(null);

  const scrollToSection = (ref) => {
    window.scrollTo({
      top: ref.current.offsetTop - 100,
      behavior: 'smooth'
    });
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.7,
        ease: "easeOut"
      }
    }
  };

  const itineraryData = [
    {
      day: 1,
      title: "Arrival in Kathmandu",
      points: [
        "Arrival at Kathmandu Airport and transfer to hotel.",
        "After check-in proceed for sightseeing.",
        "Visit Pashupatinath Temple and Boudhanath Stupa.",
        "Optional: Attend evening Aarti at Pashupatinath Temple (subject to time).",
        "Evening free for leisure at Thamel.",
        "Dinner and overnight stay in Kathmandu."
      ],
      icon: <FaBus />
    },

    {
      day: 2,
      title: "Kathmandu Sightseeing",
      points: [
        "After breakfast proceed for full-day sightseeing.",
        "Visit Swayambhunath Temple (Approx. 350 steps climb).",
        "Visit Kathmandu Durbar Square and Patan Durbar Square.",
        "Visit Budhanilkantha Temple.",
        "Evening free for shopping or café exploration.",
        "Dinner and overnight stay in Kathmandu."
      ],
      icon: <FaMapMarkerAlt />
    },

    {
      day: 3,
      title: "Kathmandu to Pokhara",
      points: [
        "After breakfast proceed to Pokhara.",
        "Distance: Approx. 200 km.",
        "Travel Time: 6–8 hours (hilly roads, may vary).",
        "En route visit Manakamana Temple.",
        "Optional cable car at additional cost.",
        "Arrival in Pokhara and hotel check-in.",
        "Evening free at Lakeside area.",
        "Dinner and overnight stay in Pokhara."
      ],
      icon: <FaGlobeAmericas />
    },

    {
      day: 4,
      title: "Pokhara Sightseeing",
      points: [
        "Early morning visit Sarangkot.",
        "After breakfast proceed for sightseeing.",
        "Visit Davis Falls and Gupteshwar Mahadev Cave.",
        "Visit Bindhyabasini Temple and Seti River Gorge.",
        "Visit Phewa Lake (Boating optional).",
        "Optional activities: Paragliding, Ziplining, ATV rides, Ultra-light flight.",
        "Dinner and overnight stay in Pokhara."
      ],
      icon: <FaMountain />
    },

    {
      day: 5,
      title: "Pokhara to Kathmandu",
      points: [
        "After breakfast proceed back to Kathmandu.",
        "Distance: Approx. 200 km.",
        "En route stop at Trishuli River.",
        "Arrival in Kathmandu.",
        "Dinner and overnight stay."
      ],
      icon: <FaBus />
    },

    {
      day: 6,
      title: "Leisure / Local Exploration",
      points: [
        "After breakfast free day for leisure.",
        "Shopping at Thamel.",
        "Café exploration and local market walk.",
        "Dinner and overnight stay in Kathmandu."
      ],
      icon: <FaStar />
    },

    {
      day: 7,
      title: "Departure",
      points: [
        "After breakfast transfer to Kathmandu Airport.",
        "Tour concludes with memorable experiences of Nepal."
      ],
      icon: <FaCheckCircle />
    }
  ];

  return (

    <div className="trip-container">

      {/* HEADER */}

      <motion.header
        initial="hidden"
        whileInView="visible"
        viewport={{ once: false }}
        variants={fadeInUp}
        className="trip-header"
      >

        <div className="header-left">

          <h1 className="trip-title">
            Himalayan Nepal Escape
          </h1>

          <p className="trip-duration">
            6 Nights / 7 Days
          </p>

        </div>

        <div className="header-right">

          <div className="info-row">

            <FaMapMarkerAlt className="header-icon" />

            <div className="info-text">
              <span>Destinations:</span>
              <p>Kathmandu – Pokhara – Sarangkot</p>
            </div>

          </div>

          <div className="info-row">

            <FaPaperPlane className="header-icon" />

            <div className="info-text">
              <span>Route:</span>
              <p>Start & End Location: Kathmandu</p>
            </div>

          </div>

          <div className="info-row type-tag">

            <FaGlobeAmericas className="header-icon" />

            <p>Spiritual & Himalayan Journey</p>

          </div>

        </div>

      </motion.header>

      {/* IMAGE GRID */}

      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: false }}
        variants={fadeInUp}
        className="image-grid-container"
      >

        <div className="grid-item large-vertical">
          <img
            src="https://images.unsplash.com/photo-1544735716-392fe2489ffa?q=80&w=1170&auto=format&fit=crop"
            alt="Nepal"
          />
        </div>

        <div className="grid-item middle-box">
          <img
            src="https://plus.unsplash.com/premium_photo-1692102550620-35f8716814b4?q=80&w=385&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
            alt="Pokhara"
          />
        </div>

        <div className="right-stack">

          <div className="grid-item small-h">
            <img
              src="https://plus.unsplash.com/premium_photo-1697729690458-2d64ca777c04?q=80&w=870&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
              alt="Kathmandu"
            />
          </div>

          <div className="grid-item small-h">
            <img
              src="https://images.unsplash.com/photo-1545309451-2369945f85a1?q=80&w=870&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
              alt="Sarangkot"
            />
          </div>

        </div>

      </motion.div>

      {/* STICKY NAV */}

      <nav className="sticky-nav">

        <button
          className="tab-link"
          onClick={() => scrollToSection(itineraryRef)}
        >
          Detailed Itinerary
        </button>

        <button
          className="tab-link"
          onClick={() => scrollToSection(highlightsRef)}
        >
          Highlights
        </button>

        <button
          className="tab-link"
          onClick={() => scrollToSection(inclusionsRef)}
        >
          Policy & Inclusions
        </button>

        <button
          className="tab-link"
          onClick={() => scrollToSection(aboutRef)}
        >
          About Trip
        </button>

      </nav>

      <div className="content-section-wrapper">

        {/* ITINERARY */}

        <section ref={itineraryRef} className="scroll-section">

          <h2 className="section-main-title">
            Tour Itinerary
          </h2>

          <div className="days-list">

            {itineraryData.map((item) => (

              <motion.div
                key={item.day}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: false, amount: 0.2 }}
                variants={fadeInUp}
                className="day-block"
              >

                <div className="day-header">

                  <span className="day-badge">
                    Day {item.day}
                  </span>

                  <h3>{item.title}</h3>

                </div>

                <div className="day-body">

                  {item.points.map((p, i) => (

                    <div key={i} className="point-item">

                      <FaChevronRight className="bullet-icon" />

                      <p>{p}</p>

                    </div>

                  ))}

                </div>

              </motion.div>

            ))}

          </div>

        </section>

        {/* HIGHLIGHTS */}

        <motion.section
          ref={highlightsRef}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false }}
          variants={fadeInUp}
          className="scroll-section"
        >

          <h2 className="section-main-title">
            Trip Highlights
          </h2>

          <div className="highlights-grid">

            <div className="h-item">
              <FaStar className="star-icon" />
              <p>Visit the sacred Pashupatinath Temple</p>
            </div>

            <div className="h-item">
              <FaStar className="star-icon" />
              <p>Experience the spiritual atmosphere at Boudhanath Stupa</p>
            </div>

            <div className="h-item">
              <FaStar className="star-icon" />
              <p>Explore Swayambhunath Temple</p>
            </div>

            <div className="h-item">
              <FaStar className="star-icon" />
              <p>Discover Kathmandu Durbar Square and Patan Durbar Square</p>
            </div>

            <div className="h-item">
              <FaStar className="star-icon" />
              <p>Visit the peaceful Budhanilkantha Temple</p>
            </div>

            <div className="h-item">
              <FaStar className="star-icon" />
              <p>Enjoy scenic drives and Himalayan views in Pokhara</p>
            </div>

            <div className="h-item">
              <FaStar className="star-icon" />
              <p>Witness sunrise views from Sarangkot</p>
            </div>

            <div className="h-item">
              <FaStar className="star-icon" />
              <p>Optional adventure activities like paragliding and ziplining</p>
            </div>

          </div>

        </motion.section>

        {/* INCLUSIONS */}

        <motion.section
          ref={inclusionsRef}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false }}
          variants={fadeInUp}
          className="scroll-section"
        >

          <div className="policy-box inclusion-box">

            <h4 className="p-title green">
              <FaCheckCircle />
              What's Included
            </h4>

            <ul className="p-list">

              <li><FaHotel /> Accommodation in hotels as per itinerary</li>

              <li><FaUtensils /> Daily breakfast and dinner</li>

              <li><FaBus /> Transportation and transfers throughout the tour</li>

              <li><FaCheckCircle /> All sightseeing as mentioned in itinerary</li>

              <li><FaShieldAlt /> Driver allowance, fuel, toll and parking charges</li>

              <li><FaCheckCircle /> Tour coordination and assistance by Chandrama Holidays</li>

            </ul>

          </div>

          <div className="policy-box exclusion-box">

            <h4 className="p-title red">
              <FaTimesCircle />
              What's Excluded
            </h4>

            <ul className="p-list">

              <li><FaTimesCircle /> Airfare / train fare</li>

              <li><FaTimesCircle /> Entry tickets to monuments and attractions</li>

              <li><FaTimesCircle /> Manakamana cable car charges</li>

              <li><FaTimesCircle /> Boating charges at Phewa Lake</li>

              <li><FaTimesCircle /> Adventure activities in Pokhara</li>

              <li><FaTimesCircle /> Lunch and additional meals</li>

              <li><FaTimesCircle /> Personal expenses like shopping and laundry</li>

              <li><FaShieldAlt /> Travel insurance</li>

              <li><FaTimesCircle /> Early check-in or late check-out</li>

              <li><FaTimesCircle /> Any cost due to weather or unforeseen circumstances</li>

            </ul>

          </div>

        </motion.section>

        {/* ABOUT */}

        <motion.section
          ref={aboutRef}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false }}
          variants={fadeInUp}
          className="scroll-section"
        >

          <h2 className="section-main-title">
            About This Journey
          </h2>

          <p className="about-text">

            Explore the perfect blend of spirituality, culture, and natural beauty with this journey through Nepal.

            <br /><br />

            From ancient temples and UNESCO heritage sites in Kathmandu to the serene lakes and Himalayan views of Pokhara, this itinerary offers a well-balanced and relaxed travel experience.

            <br /><br />

            Carefully designed with comfortable travel and meaningful sightseeing, the tour also allows optional experiences like adventure activities and cultural exploration.

          </p>

        </motion.section>

        {/* IMPORTANT NOTES */}

        <motion.section
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false }}
          variants={fadeInUp}
          className="scroll-section"
        >

          <h2 className="section-main-title">
            Important Notes
          </h2>

          <div className="policy-box inclusion-box">

            <h4 className="p-title green">
              Travel Documents
            </h4>

            <ul className="p-list">

              <li><FaCheckCircle /> Indian citizens do not require a visa for Nepal</li>

              <li><FaCheckCircle /> Carry Passport OR Voter ID Card</li>

              <li><FaTimesCircle /> Aadhaar Card and PAN Card are not accepted</li>

            </ul>

          </div>

          <div className="policy-box inclusion-box">

            <h4 className="p-title green">
              Currency
            </h4>

            <ul className="p-list">

              <li><FaCheckCircle /> Indian currency is accepted in Nepal</li>

              <li><FaCheckCircle /> ₹100, ₹200, ₹500 notes are commonly used</li>

              <li><FaCheckCircle /> Carry some Nepalese currency for convenience</li>

            </ul>

          </div>

          <div className="policy-box inclusion-box">

            <h4 className="p-title green">
              Mobile Network
            </h4>

            <ul className="p-list">

              <li><FaCheckCircle /> Indian SIM cards may have limited network</li>

              <li><FaCheckCircle /> International roaming charges may apply</li>

              <li><FaCheckCircle /> Local Nepal SIM card recommended in Kathmandu</li>

            </ul>

          </div>

        </motion.section>

        {/* TERMS */}

        <div className="accordion-item">

          <Link
            to="/terms-conditions"
            className="accordion-header"
            style={{
              textDecoration: 'none',
              color: 'inherit'
            }}
          >

            <span>Terms & Conditions</span>

            <FaChevronDown
              className="arrow-icon"
              style={{ transform: 'rotate(-90deg)' }}
            />

          </Link>

        </div>

        {/* PRIVACY */}

        <div className="accordion-item">

          <Link
            to="/privacy-policy"
            className="accordion-header"
            style={{
              textDecoration: 'none',
              color: 'inherit',
              cursor: 'pointer'
            }}
          >

            <span>Privacy Policy</span>

            <FaChevronDown
              className="arrow-icon"
              style={{ transform: 'rotate(-90deg)' }}
            />

          </Link>

        </div>

      </div>

    </div>
  );
};

export default HimalayanNepal;